// src/features/events/components/EventList.tsx

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  TablePagination,
  Paper,
  TextField,
  MenuItem,
  Autocomplete,
  Checkbox,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Favorite as HeartbeatIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import {
  useTranslate,
  usePermissions,
  useGetIdentity,
  useGetList,
  useDataProvider,
  useNotify,
  ListContextProvider,
  FunctionField,
} from 'react-admin';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { CustomDataGrid, BulkDeletePillButton } from '@/components/common';
import { HelpPopover } from '@/components/common';
import { SectionHeader } from '@/components/common/SectionHeader';
import { HotelScopeSelector } from '@/components/common/HotelScopeSelector';
import { useHotelScope } from '@/hooks/useHotelScope';
import { EventMetadataPopover } from './EventMetadataPopover';
import { MetadataSearchDialog } from './MetadataSearchDialog';
import type { Event, EventFilters, MetadataSearchParams, EventCode } from '../types';
import { getEventCodeInfo, OPERATION_EVENT_CODES, ALL_EVENT_CODES } from '../types';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { localIsoDate, localIsoDateDaysAgo } from '@/lib/localDate';

// Removable "active filter" pill shown above the log.
const ActiveFilterChip: React.FC<{ label: string; value: string; onRemove: () => void }> = ({ label, value, onRemove }) => (
  <Box sx={(t) => ({ display: 'inline-flex', alignItems: 'center', gap: 0.75, backgroundColor: t.palette.background.paper, border: `1px solid ${t.palette.divider}`, boxShadow: 'none', borderRadius: 999, pl: 1.25, pr: 0.5, py: 0.4, fontSize: 12.5, fontWeight: 600 })}>
    <Box component="span" sx={{ color: 'text.disabled' }}>{label}</Box>
    <Box component="span" sx={{ color: 'text.primary' }}>{value}</Box>
    <Box
      onClick={onRemove}
      sx={(t) => ({ width: 18, height: 18, borderRadius: '50%', display: 'grid', placeItems: 'center', cursor: 'pointer', color: t.palette.text.disabled, backgroundColor: t.palette.action.hover, '&:hover': { color: t.palette.error.main, backgroundColor: alpha(t.palette.error.main, 0.1) } })}
    >
      <ClearIcon sx={{ fontSize: 12 }} />
    </Box>
  </Box>
);

// Typeable "select": an MUI Autocomplete so the user can type to filter options.
// Clearing the field maps to '' (i.e. "all").
const TypeableSelect: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  minWidth?: number;
  disabled?: boolean;
}> = ({ label, value, onChange, options, minWidth = 150, disabled }) => {
  const selected = options.find((o) => o.value === value) || null;
  return (
    <Autocomplete
      size="small"
      disabled={disabled}
      options={options}
      value={selected}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      onChange={(_, v) => onChange(v ? v.value : '')}
      sx={{ minWidth }}
      renderInput={(params) => <TextField {...params} label={label} InputLabelProps={{ shrink: true }} />}
    />
  );
};

/**
 * Event List Component - Professional event viewer
 * Shows all events with metadata, filtering, and search capabilities
 */
export const EventList: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const dataProvider = useDataProvider();
  const { permissions } = usePermissions();
  const { data: identity, isLoading: identityLoading } = useGetIdentity();
  const location = useLocation();

  // User role checks
  const userRole = permissions || '';
  const isSuperAdmin = userRole === 'SuperAdmin' || userRole === 'Admin';
  const isGroupAdmin = userRole === 'GroupAdmin';
  const isHotelAdmin = userRole === 'HotelAdmin';
  const isReceptionist = userRole === 'Receptionist';
  const isViewer = userRole === 'Viewer';
  const assignedGroups: string[] = (identity as any)?.assignedGroups || [];
  const isMultiGroupAdmin = isGroupAdmin && assignedGroups.length > 1;

  // State
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filters state
  const [showHeartbeats, setShowHeartbeats] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [hotelId, setHotelId] = useState<string>('');
  const [deviceId, setDeviceId] = useState<string>('');
  const [eventCode, setEventCode] = useState<string>('');
  const [channel, setChannel] = useState<string>('');
  const [reservationNumber, setReservationNumber] = useState<string>('');
  const [roomNumber, setRoomNumber] = useState<string>('');

  // Shared multi-hotel scope selector (Decision 6/unit G, wave 3 follow-up): an
  // ADDITIONAL control alongside the existing group/hotel filters above. When
  // narrowed to a single hotel, pre-fills the existing `hotelId` filter (so
  // `effectiveScope` below stays untouched and both controls stay coherent);
  // a wider subset is merged into the request as `hotelIds` in `loadEvents`.
  const scope = useHotelScope();
  const scopeHotelIds = scope.selectedHotelIds && scope.selectedHotelIds.length > 1
    ? scope.selectedHotelIds.join(',')
    : undefined;
  useEffect(() => {
    const ids = scope.selectedHotelIds;
    if (ids && ids.length === 1 && ids[0] !== hotelId) {
      setHotelId(ids[0]);
      setDeviceId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.selectedHotelIds]);
  const [dateFrom, setDateFrom] = useState<string>(() => localIsoDateDaysAgo(1));
  const [dateTo, setDateTo] = useState<string>(() => localIsoDate());

  // Track if URL filters have been applied
  const urlFiltersApplied = useRef(false);

  // Parse URL filters on mount
  useEffect(() => {
    if (urlFiltersApplied.current) return;

    const searchParams = new URLSearchParams(location.search);
    const filterParam = searchParams.get('filter');

    if (filterParam) {
      try {
        const filters = JSON.parse(filterParam);

        if (filters.reservationNumber) {
          setReservationNumber(filters.reservationNumber);
          setFiltersExpanded(true);
        }
        if (filters.roomNumber) {
          setRoomNumber(filters.roomNumber);
          setFiltersExpanded(true);
        }
        if (filters.deviceId) {
          setDeviceId(filters.deviceId);
          setFiltersExpanded(true);
        }
        if (filters.hotelId) {
          setHotelId(filters.hotelId);
        }
        // Scope to a specific session's window when the link provides it
        // (e.g. "Ver eventos" from the Activity Monitor session detail).
        if (filters.dateFrom) {
          setDateFrom(filters.dateFrom);
          setFiltersExpanded(true);
        }
        if (filters.dateTo) {
          setDateTo(filters.dateTo);
        }
        if (filters.channel) {
          setChannel(filters.channel);
          setFiltersExpanded(true);
        }
        if (filters.eventCode) {
          setEventCode(filters.eventCode);
          setFiltersExpanded(true);
        }

        urlFiltersApplied.current = true;
      } catch (e) {
        console.error('Error parsing URL filter:', e);
      }
    }
  }, [location.search]);

  // Metadata search state
  const [metadataSearchOpen, setMetadataSearchOpen] = useState(false);
  const [metadataSearchActive, setMetadataSearchActive] = useState(false);
  const [metadataSearchParams, setMetadataSearchParams] = useState<MetadataSearchParams | null>(null);

  // Filters visibility
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Bulk delete state (SuperAdmin only)
  const notify = useNotify();
  const selection = useBulkSelection();

  // Load groups for multi-group GroupAdmin filter
  const { data: groups } = useGetList('groups', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: isMultiGroupAdmin ? { id: assignedGroups } : {},
  }, { enabled: isMultiGroupAdmin });

  // Hotel filter based on role
  const hotelFilter = useMemo(() => {
    if (isHotelAdmin || isReceptionist) {
      return identity?.hotelId ? { id: identity.hotelId } : {};
    } else if (isGroupAdmin) {
      if (isMultiGroupAdmin) {
        // Multi-group: filter by selected group, or no group restriction
        return selectedGroupId ? { groupId: selectedGroupId } : {};
      }
      // Single-group: filter by their one group
      return identity?.groupId ? { groupId: identity.groupId } : {};
    }
    return {};
  }, [isHotelAdmin, isReceptionist, isGroupAdmin, isMultiGroupAdmin, identity?.hotelId, identity?.groupId, selectedGroupId]);

  // Load hotels for filter
  const { data: hotels } = useGetList('hotels', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: hotelFilter,
  }, { enabled: !identityLoading });

  // Load devices for filter
  const { data: devices } = useGetList('devices', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'installationCode', order: 'ASC' },
    filter: hotelId ? { hotelId } : hotelFilter,
  }, { enabled: !identityLoading });

  // Force single-hotel roles to their own installation; every other role defaults
  // to "Todas las instalaciones" (empty hotelId), scoped server-side per role below.
  useEffect(() => {
    if ((isHotelAdmin || isReceptionist) && identity?.hotelId && !hotelId) {
      setHotelId(identity.hotelId);
    }
  }, [isHotelAdmin, isReceptionist, identity?.hotelId, hotelId]);

  // Permission-safe query scope when no specific installation is chosen. The
  // operations endpoint does NOT self-scope by permissions, so the UI must always
  // send a scope matching the user's role to avoid cross-tenant leakage. Returns
  // null when no safe scope can be determined (then we skip the query).
  const effectiveScope = useMemo<Record<string, string> | null>(() => {
    if (hotelId) return { hotelId };
    if (isHotelAdmin || isReceptionist) return identity?.hotelId ? { hotelId: identity.hotelId } : null;
    if (isSuperAdmin) return {}; // all installations
    if (isGroupAdmin) {
      const g = selectedGroupId || (identity as any)?.groupId || assignedGroups[0];
      return g ? { groupId: g } : null;
    }
    if (isViewer) {
      if ((identity as any)?.groupId) return { groupId: (identity as any).groupId };
      if (identity?.hotelId) return { hotelId: identity.hotelId };
      return null;
    }
    return null;
  }, [hotelId, isHotelAdmin, isReceptionist, isSuperAdmin, isGroupAdmin, isViewer, selectedGroupId, identity, assignedGroups]);

  // Load events
  const loadEvents = useCallback(async () => {
    if (!effectiveScope) return;

    setLoading(true);
    setError(null);

    try {
      // If metadata search is active, use that endpoint
      if (metadataSearchActive && metadataSearchParams) {
        const result = await dataProvider.searchEventsByMetadata({
          ...metadataSearchParams,
          ...effectiveScope,
          ...(scopeHotelIds ? { hotelIds: scopeHotelIds } : {}),
          page: page + 1,
          pageSize: rowsPerPage,
        });
        setEvents(result.data.items || []);
        setTotal(result.data.totalItems || 0);
      } else {
        // Raw received-event feed (includes section navigation, payment, key
        // delivery, errors and abandonment — not just the aggregated operations).
        const filter: any = {
          ...effectiveScope,
          ...(scopeHotelIds ? { hotelIds: scopeHotelIds } : {}),
        };

        if (deviceId) filter.deviceId = deviceId;
        if (channel) filter.channel = channel;
        if (reservationNumber) filter.reservationNumber = reservationNumber;
        if (roomNumber) filter.roomNumber = roomNumber;
        if (dateFrom) filter.dateFrom = dateFrom;
        if (dateTo) filter.dateTo = dateTo;

        // The raw feed matches the event code exactly (no operation-type mapping).
        if (eventCode) filter.eventCode = eventCode;

        // Heartbeats are dropped by default (high-volume pings); the toggle
        // brings them back. Ignored server-side when a specific eventCode is set.
        if (!showHeartbeats) {
          filter.excludeHeartbeats = true;
        }

        const result = await dataProvider.getList('events', {
          pagination: { page: page + 1, perPage: rowsPerPage },
          sort: { field: 'createdAt', order: 'DESC' },
          filter,
        });

        setEvents(result.data || []);
        setTotal(result.total || 0);
      }
    } catch (err: any) {
      console.error('Error loading events:', err);
      setError(err.message || translate('resources.events.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [
    dataProvider, effectiveScope, scopeHotelIds, deviceId, channel, reservationNumber, roomNumber, dateFrom, dateTo,
    eventCode, showHeartbeats, page, rowsPerPage, metadataSearchActive, metadataSearchParams, translate
  ]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Handlers
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMetadataSearch = (params: MetadataSearchParams) => {
    setMetadataSearchParams(params);
    setMetadataSearchActive(true);
    setPage(0);
  };

  const handleClearMetadataSearch = () => {
    setMetadataSearchParams(null);
    setMetadataSearchActive(false);
    setPage(0);
  };

  // Bulk delete handlers (SuperAdmin only)
  const handleDeleteSelected = async () => {
    try {
      await dataProvider.deleteMany('operations', { ids: selection.selectedIds });
      selection.clearSelection();
      loadEvents();
    } catch (err: any) {
      notify(translate('resources.events.bulkDelete.errorMessage'), { type: 'error' });
      throw err;
    }
  };

  const handleDeleteEventsByFilters = async () => {
    const filterPayload: Record<string, string | undefined> = {
      hotelId: hotelId || undefined,
      deviceId: deviceId || undefined,
      channel: channel || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      eventCode: eventCode || undefined,
      reservationNumber: reservationNumber || undefined,
      roomNumber: roomNumber || undefined,
    };
    const result = await dataProvider.deleteEventsByFilter(filterPayload);
    selection.clearSelection();
    loadEvents();
    return { deletedCount: result.data.deletedCount };
  };

  // Loading state — wait only for identity; an empty hotel means "all installations".
  if (identityLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // List context for CustomDataGrid
  const listContext = {
    data: events,
    total,
    isLoading: loading,
    page: page + 1,
    perPage: rowsPerPage,
    setPage: (p: number) => setPage(p - 1),
    setPerPage: setRowsPerPage,
    sort: { field: 'createdAt', order: 'DESC' as const },
    filterValues: {},
    resource: 'events',
    selectedIds: [],
    onSelect: () => {},
    onToggleItem: () => {},
    onUnselectItems: () => {},
    refetch: loadEvents,
  };

  // Check if any advanced filter is active
  const hasAdvancedFilters = reservationNumber || roomNumber || deviceId || eventCode || channel;
  const eventsActiveFilterCount = [
    reservationNumber, roomNumber, deviceId, eventCode, channel,
    // A specific installation counts as a filter only for roles that can pick one
    // (single-hotel roles are always pinned to their own).
    (!isHotelAdmin && !isReceptionist) ? hotelId : '',
  ].filter(Boolean).length;

  return (
    <Box sx={{ p: { xs: 1, sm: 1 } }}>
      {/* Standard header card: no tabs (single view) — Help in the left zone,
          filters in the popover, Refresh + bulk delete in the right cluster. */}
      <SectionHeader manualScreen="events"
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <HelpPopover text={translate('resources.events.help.page')} />
            <HotelScopeSelector scope={scope} />
          </Box>
        }
        activeFilterCount={eventsActiveFilterCount}
        filtersOpen={filtersExpanded}
        onFiltersOpenChange={setFiltersExpanded}
        onRefresh={() => loadEvents()}
        refreshing={loading}
        extraActions={isSuperAdmin ? (
          <BulkDeletePillButton
            selectedCount={selection.count}
            onDeleteSelected={handleDeleteSelected}
            onDeleteByFilters={handleDeleteEventsByFilters}
            resourceLabel="events"
          />
        ) : undefined}
        filterContent={
          <>
          {/* Group Filter (multi-group GroupAdmin only) */}
          {isMultiGroupAdmin && (
            <TypeableSelect
              label={translate('resources.events.filters.group')}
              value={selectedGroupId}
              onChange={(v) => { setSelectedGroupId(v); setHotelId(''); setDeviceId(''); setPage(0); }}
              options={(groups || []).map((g: any) => ({ value: g.id, label: g.name }))}
              minWidth={150}
            />
          )}

          {/* Hotel Filter — single-hotel roles are pinned; everyone else can pick a
              specific installation or "Todas las instalaciones" (empty value). */}
          <TypeableSelect
            label={translate('resources.events.filters.hotel')}
            value={hotelId}
            onChange={(v) => { setHotelId(v); setDeviceId(''); setPage(0); }}
            options={[
              ...((isHotelAdmin || isReceptionist)
                ? []
                : [{ value: '', label: translate('resources.events.filters.allHotels', { _: 'Todas las instalaciones' }) }]),
              ...(hotels || []).map((h: any) => ({ value: h.id, label: h.name })),
            ]}
            minWidth={230}
            disabled={isHotelAdmin || isReceptionist}
          />

          {/* Date From */}
          <TextField
            type="date"
            size="small"
            label={translate('resources.events.filters.dateFrom')}
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />

          {/* Date To */}
          <TextField
            type="date"
            size="small"
            label={translate('resources.events.filters.dateTo')}
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />

          {/* Device */}
          <TypeableSelect
            label={translate('resources.events.filters.device')}
            value={deviceId}
            onChange={(v) => { setDeviceId(v); setPage(0); }}
            options={(devices || []).map((d: any) => ({ value: d.installationCode, label: d.name ? `${d.installationCode} - ${d.name}` : d.installationCode }))}
            minWidth={150}
          />

          {/* Event Code */}
          <TypeableSelect
            label={translate('resources.events.filters.eventCode')}
            value={eventCode}
            onChange={(v) => { setEventCode(v); setPage(0); }}
            options={ALL_EVENT_CODES.map((code) => ({ value: code, label: translate(`resources.events.eventCodes.${code}`) }))}
            minWidth={150}
          />

          {/* Channel */}
          <TypeableSelect
            label={translate('resources.events.filters.channel')}
            value={channel}
            onChange={(v) => { setChannel(v); setPage(0); }}
            options={['Kiosk', 'Dispenser', 'Online', 'AirLobby', 'MobileKiosk'].map((c) => ({ value: c, label: translate(`resources.events.channels.${c}`) }))}
            minWidth={150}
          />

          {/* Reservation search */}
          <TextField
            size="small"
            label={translate('resources.events.filters.reservation')}
            value={reservationNumber}
            onChange={(e) => { setReservationNumber(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 130 }}
          />

          {/* Room number search */}
          <TextField
            size="small"
            label={translate('resources.events.filters.roomNumber')}
            value={roomNumber}
            onChange={(e) => { setRoomNumber(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 100 }}
          />

          {/* Metadata Search Button — pill style matching AdminOptionsPage/DeviceList */}
          <Button
            onClick={() => setMetadataSearchOpen(true)}
            size="small"
            variant={metadataSearchActive ? 'contained' : 'outlined'}
            startIcon={<SearchIcon sx={{ fontSize: '14px !important' }} />}
            sx={{
              fontSize: '0.75rem',
              py: 0.3,
              px: 1.5,
              minHeight: '30px',
              borderRadius: '16px',
              textTransform: 'none',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              ...(metadataSearchActive
                ? {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.dark' },
                  }
                : {
                    borderColor: 'grey.300',
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    },
                  }),
            }}
          >
            {translate('resources.events.metadata.searchButton')}
          </Button>

          {/* Show Heartbeats Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={showHeartbeats}
                onChange={(e) => { setShowHeartbeats(e.target.checked); setPage(0); }}
                size="small"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <HeartbeatIcon sx={{ fontSize: 14, color: showHeartbeats ? 'error.main' : 'text.secondary' }} />
                <Typography sx={{ fontSize: '0.75rem' }}>
                  {translate('resources.events.filters.showHeartbeats')}
                </Typography>
              </Box>
            }
            sx={{ ml: 1, mr: 0 }}
          />

          {/* Clear filters button */}
          {hasAdvancedFilters && (
            <Button
              size="small"
              startIcon={<ClearIcon sx={{ fontSize: '14px !important' }} />}
              onClick={() => {
                setReservationNumber('');
                setRoomNumber('');
                setDeviceId('');
                setEventCode('');
                setChannel('');
                setPage(0);
              }}
              sx={{ fontSize: '0.75rem', textTransform: 'none' }}
            >
              {translate('ra.action.clear_input_value')}
            </Button>
          )}
          </>
        }
      />

      {/* Active filter chips */}
      {hasAdvancedFilters && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
          {channel && (
            <ActiveFilterChip
              label={translate('resources.events.fields.channel')}
              value={translate(`resources.events.channels.${channel}`, { _: channel })}
              onRemove={() => { setChannel(''); setPage(0); }}
            />
          )}
          {eventCode && (
            <ActiveFilterChip
              label={translate('resources.events.fields.eventCode')}
              value={translate(`resources.events.eventCodes.${eventCode}`, { _: eventCode })}
              onRemove={() => { setEventCode(''); setPage(0); }}
            />
          )}
          {deviceId && (
            <ActiveFilterChip
              label={translate('resources.events.fields.device')}
              value={(() => { const dv = devices?.find((d: any) => d.installationCode === deviceId); return dv?.name ? `${deviceId} · ${dv.name}` : deviceId; })()}
              onRemove={() => { setDeviceId(''); setPage(0); }}
            />
          )}
          {reservationNumber && (
            <ActiveFilterChip
              label={translate('resources.events.fields.reservation')}
              value={reservationNumber}
              onRemove={() => { setReservationNumber(''); setPage(0); }}
            />
          )}
          {roomNumber && (
            <ActiveFilterChip
              label={translate('resources.events.filters.roomNumber')}
              value={roomNumber}
              onRemove={() => { setRoomNumber(''); setPage(0); }}
            />
          )}
          <Box
            onClick={() => { setReservationNumber(''); setRoomNumber(''); setDeviceId(''); setEventCode(''); setChannel(''); setPage(0); }}
            sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.secondary', cursor: 'pointer', px: 1, py: 0.5, borderRadius: 1, '&:hover': { color: 'primary.dark' } }}
          >
            {translate('ra.action.clear_input_value', { _: 'Limpiar' })}
          </Box>
        </Box>
      )}

      {/* Metadata search active indicator */}
      {metadataSearchActive && metadataSearchParams && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleClearMetadataSearch}>
              {translate('ra.action.clear_input_value')}
            </Button>
          }
        >
          {translate('resources.events.metadata.searchActive', {
            key: metadataSearchParams.key,
            value: metadataSearchParams.value,
          })}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Event log — shared datagrid recipe: desktop table / mobile cards,
          the frame's own empty state, skeleton on first load (F-B-010/011) */}
      <Box
        sx={{
          backgroundColor: 'background.paper',
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: theme.custom?.radius?.card ?? '10px',
          boxShadow: 'none',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ListContextProvider value={listContext as any}>
          <CustomDataGrid
            bulkActionButtons={false}
            primarySource="eventCode"
            secondarySource="createdAt"
            sx={{ border: 'none', borderRadius: 0 }}
          >
            {isSuperAdmin && (
              <FunctionField
                label={
                  <Checkbox
                    size="small"
                    sx={{ p: 0 }}
                    checked={events.length > 0 && selection.allSelected(events.map((e) => e.id))}
                    indeterminate={selection.count > 0 && !selection.allSelected(events.map((e) => e.id))}
                    onChange={() => selection.selectAll(events.map((e) => e.id))}
                  />
                }
                render={(record: any) => (
                  <Checkbox
                    size="small"
                    sx={{ p: 0 }}
                    checked={selection.isSelected(record.id)}
                    onChange={() => selection.toggle(record.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              />
            )}

            <FunctionField
              source="createdAt"
              label={translate('resources.events.fields.createdAt')}
              render={(record: any) => (
                <Typography sx={{ fontFamily: 'monospace', fontSize: 12.5, color: 'text.secondary', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {format(new Date(record.createdAt), 'dd/MM')}&nbsp;{format(new Date(record.createdAt), 'HH:mm:ss')}
                </Typography>
              )}
            />

            <FunctionField
              source="eventCode"
              label={translate('resources.events.fields.eventCode')}
              render={(record: any) => {
                const label = translate(`resources.events.eventCodes.${record.eventCode}`, { _: record.eventCode });
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Typography>
                    {label !== record.eventCode && (
                      <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600, color: 'text.disabled', backgroundColor: 'action.hover', border: `1px solid ${theme.palette.divider}`, px: 1, py: '2px', borderRadius: '6px', flexShrink: 0 }}>
                        {record.eventCode}
                      </Box>
                    )}
                  </Box>
                );
              }}
            />

            <FunctionField
              source="deviceId"
              label={translate('resources.events.fields.device')}
              render={(record: any) => {
                const dev = devices?.find((d: any) => d.installationCode === record.deviceId);
                const deviceDisplay = dev?.name ? `${record.deviceId} · ${dev.name}` : record.deviceId;
                return (
                  <Typography sx={{ fontFamily: 'monospace', fontSize: 12.5, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{deviceDisplay}</Typography>
                );
              }}
            />

            <FunctionField
              source="reservationNumber"
              label={translate('resources.events.fields.reservation')}
              render={(record: any) => {
                const res = record.reservationNumber;
                const room = record.metadata?.roomNumber || record.metadata?.keyDispensedRoom;
                if (!res && !room) return <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>—</Typography>;
                return (
                  <Box sx={{ overflow: 'hidden' }}>
                    {res && <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>{res}</Typography>}
                    {room && <Typography sx={{ fontSize: 11.5, color: 'text.disabled', lineHeight: 1.2 }}>Hab. {room}</Typography>}
                  </Box>
                );
              }}
            />

            {(isSuperAdmin || isGroupAdmin) && (
              <FunctionField
                source="hotelId"
                label={translate('resources.events.fields.hotel')}
                render={(record: any) => {
                  const hotel = hotels?.find((h: any) => h.id === record.hotelId);
                  return (
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{hotel?.name || record.hotelId}</Typography>
                  );
                }}
              />
            )}

            <FunctionField
              source="channel"
              label={translate('resources.events.fields.channel')}
              render={(record: any) => (
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                  {translate(`resources.events.channels.${record.channel}`, { _: record.channel })}
                </Typography>
              )}
            />

            <FunctionField
              source="metadata"
              label={translate('resources.events.fields.metadata')}
              render={(record: any) => (
                <EventMetadataPopover
                  metadata={record.metadata}
                  eventCode={record.eventCode}
                  errorCode={record.errorCode}
                  excludeKeys={['reservationNumber', 'roomNumber', 'keyDispensedRoom']}
                />
              )}
            />
          </CustomDataGrid>
        </ListContextProvider>

        {/* Pagination */}
        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={translate('ra.navigation.page_rows_per_page')}
          />
        </Box>
      </Box>

      {/* Metadata Search Dialog */}
      <MetadataSearchDialog
        open={metadataSearchOpen}
        onClose={() => setMetadataSearchOpen(false)}
        onSearch={handleMetadataSearch}
        initialParams={metadataSearchParams || undefined}
      />

    </Box>
  );
};

export default EventList;
